/**
 * ProposedDocuments — Owen's dynamic "Documents to Prove This" panel.
 *
 * Reads `product_document_conditions` for the recommended program and matches
 * conditions against the borrower's stated application data. Whenever the user
 * changes anything in the URLA, this component recomputes the list of proof
 * documents Owen will need.
 *
 * Two render modes:
 *   - compact:  right-rail companion alongside the URLA form (badges only)
 *   - full:     standalone panel for the document upload step (categorized,
 *               with Owen's "why we need this" rationale per item)
 *
 * Replaces the prior react-bootstrap implementation with LK design-system primitives.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Surface, Badge, Spinner } from '../../ui/components';
import {
  FolderOpen, FileText, Info, ChevronDown, ChevronUp, Sparkles,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

// Convention used by the existing seed data: when a condition's `field_name` is
// a multi-entry section's "type" attribute, we map it here.
const FIELD_TO_TYPE_MAP = {
  assetTypes: 'assetType',
  liabilityTypes: 'liabilityType',
  otherIncomeSources: 'incomeSourceType',
  creditsTypes: 'creditType',
  giftsOrGrants: 'giftSource',
  citizenship: 'citizenship',
  maritalStatus: 'maritalStatus',
  employmentType: 'employmentType',
  incomeTypes: 'incomeTypes',
};

const MULTI_ENTRY_SECTIONS = [
  'assets', 'liabilities', 'otherIncome', 'credits',
  'giftsGrants', 'borrowerInfo', 'employmentIncome',
];

const CATEGORY_RULES = [
  {
    label: 'Income & Employment',
    match: /tax|w-?2|pay|income|employment|1099|p&l|profit|business/i,
  },
  {
    label: 'Assets & Accounts',
    match: /bank|statement|asset|401k|ira|stock|account|retirement|gift/i,
  },
  {
    label: 'Identity & Legal',
    match: /id\b|passport|visa|green card|divorce|death|certificate|dd-?214|coe/i,
  },
  {
    label: 'Property',
    match: /appraisal|contract|title|insurance|hoa|property/i,
  },
];

const categorize = (name) =>
  CATEGORY_RULES.find((r) => r.match.test(name))?.label || 'Other';

// Friendly "why we need this" rationale, derived from the matched condition source.
function buildReason(sources = []) {
  if (sources.length === 0) return null;
  const phrases = sources.map((s) => {
    if (!s.value) return null;
    return `you stated ${String(s.value).toLowerCase()}`;
  }).filter(Boolean);
  if (phrases.length === 0) return null;
  return `Owen needs this because ${phrases.slice(0, 3).join(' and ')}.`;
}

const ProposedDocuments = ({
  applicationData = null,
  productId = null,
  compact = false,
  uploadedDocs = {},
  onUploadClick = null,
}) => {
  const reduxApplicationData = useSelector((s) => s.loanApplication?.applicationData || {});
  const reduxProductId = useSelector((s) => s.loanApplication?.recommendedProduct?.id);

  const effectiveData = applicationData && Object.keys(applicationData).length > 0
    ? applicationData
    : reduxApplicationData;
  const effectiveProductId = productId || reduxProductId;

  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchConditions = async () => {
      if (!effectiveProductId) {
        setConditions([]);
        return;
      }
      setLoading(true);
      try {
        let actualProductId = effectiveProductId;
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
          .test(String(effectiveProductId));

        if (!isUUID) {
          const { data: productData } = await supabase
            .from('loan_products')
            .select('id')
            .or(`short_name.ilike.%${effectiveProductId}%,name.ilike.%${effectiveProductId}%`)
            .limit(1)
            .single();
          if (!productData) {
            if (!cancelled) setConditions([]);
            return;
          }
          actualProductId = productData.id;
        }

        const { data, error } = await supabase
          .from('product_document_conditions')
          .select('*')
          .eq('product_id', actualProductId);
        if (error) throw error;
        if (!cancelled) setConditions(data || []);
      } catch (err) {
        console.warn('Document conditions fetch failed:', err?.message);
        if (!cancelled) setConditions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchConditions();
    return () => { cancelled = true; };
  }, [effectiveProductId]);

  const requiredDocuments = useMemo(() => {
    const docs = new Map();
    const add = (cond, sourceLabel) => {
      (cond.required_documents || []).forEach((doc) => {
        if (!docs.has(doc)) docs.set(doc, { name: doc, sources: [] });
        docs.get(doc).sources.push({
          field: cond.field_name,
          value: sourceLabel || cond.field_value,
        });
      });
    };

    conditions.forEach((cond) => {
      const direct = effectiveData?.[cond.field_name];
      if (Array.isArray(direct) && direct.includes(cond.field_value)) add(cond);
      else if (direct === cond.field_value) add(cond);

      MULTI_ENTRY_SECTIONS.forEach((sectionKey) => {
        const sectionData = effectiveData?.[sectionKey];
        if (!sectionData?.entries) return;
        sectionData.entries.forEach((entry) => {
          const typeFieldId = FIELD_TO_TYPE_MAP[cond.field_name];
          if (!typeFieldId) return;
          const entryValue = entry?.[typeFieldId];
          const matches = Array.isArray(entryValue)
            ? entryValue.includes(cond.field_value)
            : entryValue === cond.field_value;
          if (!matches) return;
          const entryLabel =
            (entry.firstName && entry.lastName && `${entry.firstName} ${entry.lastName}`) ||
            entry.employerName || entry.institutionName || entry.creditorName ||
            entry.donorName || 'Entry';
          add(cond, `${cond.field_value} (${entryLabel})`);
        });
      });
    });

    return Array.from(docs.values());
  }, [conditions, effectiveData]);

  const grouped = useMemo(() => {
    const out = new Map();
    requiredDocuments.forEach((d) => {
      const key = categorize(d.name);
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(d);
    });
    return Array.from(out.entries());
  }, [requiredDocuments]);

  // ── Empty / loading states ──
  if (loading) {
    return (
      <Surface padding="var(--lk-space-3)">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Spinner size="sm" />
          <span style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>
            Owen is calculating required documents…
          </span>
        </div>
      </Surface>
    );
  }

  if (!effectiveProductId) {
    return (
      <Surface padding="var(--lk-space-3)">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <FolderOpen size={16} color="var(--lk-text-tertiary)" />
          <span style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 600 }}>
            Documents to Prove It
          </span>
        </div>
        <p style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)', margin: 0 }}>
          Pick a loan program and Owen will list the documents you'll need.
        </p>
      </Surface>
    );
  }

  // ── Compact mode (right rail) ──
  if (compact) {
    return (
      <Surface padding="var(--lk-space-3)">
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FolderOpen size={16} color="var(--lk-primary-600)" />
            <span style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 600 }}>
              Documents to Prove It
            </span>
          </div>
          <Badge variant="primary">{requiredDocuments.length}</Badge>
        </div>

        {requiredDocuments.length === 0 ? (
          <p style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)', margin: 0 }}>
            As you fill out the application, Owen will list documents needed to verify each item.
          </p>
        ) : (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginBottom: 8, fontSize: 'var(--lk-text-xs)',
              color: 'var(--lk-text-tertiary)',
            }}>
              <Sparkles size={12} color="var(--lk-primary-500)" />
              <span>Updates as you fill in your application.</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {requiredDocuments.slice(0, 6).map((d, i) => (
                <Badge key={i} variant="neutral" style={{ fontWeight: 400 }}>
                  {d.name}
                </Badge>
              ))}
              {requiredDocuments.length > 6 && (
                <Badge variant="primary">+{requiredDocuments.length - 6} more</Badge>
              )}
            </div>
          </>
        )}
      </Surface>
    );
  }

  // ── Full mode (used on the document upload step) ──
  return (
    <Surface padding={0} style={{ overflow: 'hidden' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--lk-space-4) var(--lk-space-5)',
          backgroundColor: 'var(--lk-primary-600)', color: 'white',
          border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FolderOpen size={20} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 'var(--lk-text-md)' }}>
              Documents to Prove It
            </div>
            <div style={{ fontSize: 'var(--lk-text-xs)', opacity: 0.85 }}>
              Owen built this list from what you've stated.
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Badge variant="neutral" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}>
            {requiredDocuments.length} document{requiredDocuments.length === 1 ? '' : 's'}
          </Badge>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>

      {expanded && (
        <div style={{ padding: 'var(--lk-space-5)' }}>
          {requiredDocuments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--lk-space-6)' }}>
              <Info size={32} color="var(--lk-text-tertiary)" />
              <p style={{ marginTop: 12, fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>
                Once you complete your application sections, Owen will list the exact documents
                you'll need for this program.
              </p>
            </div>
          ) : (
            grouped.map(([cat, docs], gi) => (
              <div key={cat} style={{ marginBottom: gi < grouped.length - 1 ? 20 : 0 }}>
                <div style={{
                  fontSize: 'var(--lk-text-xs)', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.6px',
                  color: 'var(--lk-text-tertiary)', marginBottom: 8,
                }}>
                  {cat}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {docs.map((doc) => {
                    const isUploaded = !!uploadedDocs?.[doc.name];
                    const reason = buildReason(doc.sources);
                    return (
                      <div
                        key={doc.name}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10,
                          padding: 12, borderRadius: 8,
                          backgroundColor: isUploaded ? 'var(--lk-success-50)' : 'var(--lk-bg-surface-2)',
                          border: isUploaded
                            ? '1px solid var(--lk-success-300)'
                            : '1px solid var(--lk-border-subtle)',
                        }}
                      >
                        <FileText
                          size={16}
                          color={isUploaded ? 'var(--lk-success-600)' : 'var(--lk-primary-500)'}
                          style={{ marginTop: 2, flexShrink: 0 }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 600 }}>
                            {doc.name}
                          </div>
                          {reason && (
                            <div style={{
                              fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)',
                              marginTop: 2, display: 'flex', gap: 4, alignItems: 'flex-start',
                            }}>
                              <Sparkles size={10} color="var(--lk-primary-500)" style={{ marginTop: 2, flexShrink: 0 }} />
                              <span>{reason}</span>
                            </div>
                          )}
                        </div>
                        {onUploadClick && (
                          <button
                            onClick={() => onUploadClick(doc.name)}
                            style={{
                              fontSize: 12, fontWeight: 600,
                              padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                              border: '1px solid var(--lk-border)',
                              backgroundColor: isUploaded ? 'var(--lk-success-100)' : 'var(--lk-bg-surface-1)',
                              color: isUploaded ? 'var(--lk-success-700)' : 'var(--lk-text-primary)',
                            }}
                          >
                            {isUploaded ? 'Replace' : 'Upload'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </Surface>
  );
};

export default ProposedDocuments;
